/**
 * Banner mostrado no topo do Flow Builder quando o gatilho do fluxo é
 * "webhook externo". Mostra a URL do webhook pronta para copiar — usuário
 * não precisa sair pra outra tela.
 *
 * Faz lookup do webhook vinculado ao flow via `webhooks.list`. Se não houver
 * webhook vinculado (caso de fluxo criado antes do F0.3, ou webhook
 * deletado), oferece botão "Criar webhook agora".
 */

import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Globe, Copy, Check, ExternalLink, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

type Props = {
  flowId: number;
  flowName: string;
};

export default function WebhookTriggerBanner({ flowId, flowName }: Props) {
  const utils = trpc.useUtils();
  const { data: webhooks = [], isLoading } = trpc.webhooks.list.useQuery();
  const webhook = useMemo(
    () => webhooks.find((w) => w.flowId === flowId),
    [webhooks, flowId],
  );

  const [copied, setCopied] = useState(false);
  const baseUrl =
    typeof window !== "undefined" ? window.location.origin : "";
  const fullUrl = webhook ? `${baseUrl}/api/webhooks/in/${webhook.urlToken}` : "";

  const createMut = trpc.webhooks.create.useMutation({
    onSuccess: () => {
      utils.webhooks.list.invalidate();
      toast.success("Webhook criado e vinculado ao fluxo");
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Falha ao criar o webhook.")),
  });
  const rotateMut = trpc.webhooks.rotate.useMutation({
    onSuccess: () => {
      utils.webhooks.list.invalidate();
      toast.success("Token rotacionado. Antigo aceito por 7 dias.");
    },
    // M-2 — Sem onError, falha de rotação ficava silenciosa.
    onError: (err) => toast.error(toUserFriendlyError(err, "Falha ao rotacionar o token.")),
  });

  function copyUrl() {
    if (!fullUrl) return;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  if (isLoading) return null;

  // Sem webhook vinculado — oferece criar.
  if (!webhook) {
    return (
      <div className="border-b bg-amber-50 dark:bg-amber-950/20 px-4 py-2 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
          <span className="text-amber-900 dark:text-amber-200 truncate">
            Este fluxo tem gatilho <strong>Webhook</strong> mas ainda não tem
            URL configurada. Crie agora para receber dados de sistemas externos.
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          disabled={createMut.isPending}
          onClick={() =>
            createMut.mutate({
              name: `Webhook — ${flowName}`,
              flowId,
              isActive: true,
            })
          }
        >
          {createMut.isPending ? "Criando…" : "Criar webhook agora"}
        </Button>
      </div>
    );
  }

  return (
    <div className="border-b bg-blue-50 dark:bg-blue-950/20 px-4 py-2 flex items-center gap-3 text-sm">
      <Globe className="w-4 h-4 text-blue-600 shrink-0" />
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="text-blue-900 dark:text-blue-200 shrink-0">URL:</span>
        <code className="text-xs font-mono bg-white dark:bg-blue-950/40 px-2 py-1 rounded border border-blue-200 dark:border-blue-900/40 truncate flex-1 min-w-0">
          {fullUrl}
        </code>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-xs text-muted-foreground hidden sm:inline">
          {webhook.requestCount}/{webhook.requestLimit}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={copyUrl}
          className="gap-1.5 h-7"
        >
          {copied ? (
            <Check className="w-3.5 h-3.5 text-green-600" />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
          {copied ? "Copiado" : "Copiar"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="gap-1.5 h-7"
          title="Rotacionar token (gera nova URL; antiga aceita por 7 dias)"
          onClick={() => {
            if (
              confirm(
                "Rotacionar token? O sistema externo precisará atualizar a URL nos próximos 7 dias (grace period).",
              )
            ) {
              rotateMut.mutate({ id: webhook.id, gracePeriodHours: 168 });
            }
          }}
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
        <a
          href="/settings?tab=integrations&integration=webhooks"
          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
          title="Gerenciar todos os webhooks em Configurações > Integrações"
        >
          <ExternalLink className="w-3 h-3" />
          <span className="hidden md:inline">Gerenciar</span>
        </a>
      </div>
    </div>
  );
}
